import React, { useState } from 'react';
import StatusBadge from './StatusBadge';
import { javaApi } from '../services/api';

const ReusableCodeExecutor = ({ 
  defaultCode = `public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        System.out.println("Welcome to Java Code Executor!");
    }
}`,
  className = "",
  onCodeChange = null,
  onExecutionComplete = null
}) => {
  const [code, setCode] = useState(defaultCode);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showInput, setShowInput] = useState(false);

  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    setCode(newCode);
    if (validationError) {
      setValidationError('');
    }
    if (onCodeChange) {
      onCodeChange(newCode);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleRun = async () => {
    if (!code.trim()) {
      setValidationError('Please enter some Java code to execute.');
      return;
    }

    setValidationError('');
    setIsLoading(true);
    setOutput('Running code...');
    setStatus('');

    try {
      const response = await javaApi.executeCode(code, input);
      setOutput(response.output || 'No output');
      setStatus(response.status || 'success');
      
      if (!response.success) {
        setStatus(response.status || 'error');
      }

      if (onExecutionComplete) {
        onExecutionComplete({
          success: response.success,
          output: response.output,
          status: response.status
        });
      }
    } catch (error) {
      console.error('Execution failed:', error);
      
      let errorMessage = 'Unexpected error: Please try again';
      let errorStatus = 'error';
      
      if (error.response) {
        const { status: statusCode, data } = error.response;
        if (statusCode === 400) {
          errorMessage = data.output || 'Bad request: Invalid input';
        } else if (statusCode >= 500) {
          errorMessage = 'Server error: Please try again later';
        } else {
          errorMessage = data.output || `Error: ${statusCode}`;
          errorStatus = data.status || 'error';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      }
      
      setOutput(errorMessage);
      setStatus(errorStatus);

      if (onExecutionComplete) {
        onExecutionComplete({
          success: false,
          output: errorMessage,
          status: errorStatus
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetCode = () => {
    setCode(defaultCode);
    setInput('');
    setOutput('');
    setStatus('');
    setValidationError('');
    if (onCodeChange) {
      onCodeChange(defaultCode);
    }
  };

  return (
    <div className={`reusable-code-executor ${className}`}>
      <div className="executor-grid">
        {/* Code Editor Panel */}
        <div className="editor-panel">
          <div className="panel-header">
            <h3>Java Code</h3>
            <div className="editor-actions">
              <button 
                onClick={resetCode}
                disabled={isLoading}
                className="reset-btn"
                title="Reset to default code"
              >
                Reset
              </button>
            </div>
          </div>
          <textarea
            value={code}
            onChange={handleCodeChange}
            placeholder="Enter your Java code here..."
            className={`code-textarea ${validationError ? 'error' : ''}`}
            spellCheck={false}
          />
          {validationError && <div className="validation-error">{validationError}</div>}
        </div>

        {/* Input Panel Toggle */}
        <div className="input-toggle-container">
          <button 
            className="input-toggle-btn"
            onClick={() => setShowInput(!showInput)}
          >
            {showInput ? '− Hide Input' : '+ Add Input'}
          </button>
        </div>

        {/* Input Panel */}
        {showInput && (
          <div className="input-panel">
            <h3>Input (Optional)</h3>
            <textarea
              value={input}
              onChange={handleInputChange}
              placeholder="Enter input for your program..."
              className="input-textarea"
              spellCheck={false}
            />
          </div>
        )}

        {/* Action Panel */}
        <div className="action-panel">
          <button
            onClick={handleRun}
            disabled={isLoading}
            className="run-btn"
          >
            {isLoading ? 'Running...' : 'Run Code'}
          </button>
        </div>

        {/* Output Panel */}
        <div className="output-panel">
          <div className="output-header">
            <h3>Output</h3>
            {status && !isLoading && <StatusBadge status={status} />}
          </div>
          <div className={`output-textarea ${status?.toLowerCase()} ${isLoading ? 'loading' : ''}`}>
            {isLoading ? 'Running...' : output || 'Results will appear here...'}
          </div>
        </div>
      </div>

      <style jsx>{`
        .reusable-code-executor {
          width: 100%;
          max-width: 100%;
          padding: 32px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(226, 232, 240, 0.8);
        }

        .executor-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          margin-bottom: 28px;
        }

        .editor-panel {
          grid-column: 1 / -1;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(226, 232, 240, 0.6);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .editor-panel:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
        }

        .panel-header h3 {
          margin: 0;
          color: #0f172a;
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .editor-actions {
          display: flex;
          gap: 12px;
        }

        .reset-btn {
          background: linear-gradient(135deg, #64748b 0%, #475569 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(100, 116, 139, 0.3);
          letter-spacing: 0.02em;
        }

        .reset-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #475569 0%, #334155 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(100, 116, 139, 0.4);
        }

        .reset-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .code-textarea,
        .input-textarea {
          width: 100%;
          min-height: 200px;
          padding: 20px;
          border: 2px solid rgba(226, 232, 240, 0.8);
          border-radius: 12px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Fira Code', monospace;
          font-size: 14px;
          line-height: 1.7;
          resize: vertical;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: #1e293b;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .code-textarea:focus,
        .input-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.02);
          background: white;
        }

        .input-textarea {
          min-height: 140px;
        }

        .code-textarea.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.02);
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }

        .validation-error {
          color: #dc2626;
          font-size: 14px;
          margin-top: 14px;
          padding: 12px 16px;
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          border: 1px solid #fecaca;
          border-radius: 10px;
          font-weight: 600;
          animation: slideDown 0.3s ease;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.1);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .input-panel {
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(226, 232, 240, 0.6);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .input-panel:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .input-panel h3 {
          margin: 0 0 20px 0;
          color: #0f172a;
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          padding-bottom: 16px;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .input-toggle-container {
          grid-column: 1 / -1;
          margin-bottom: 24px;
        }

        .input-toggle-btn {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
          letter-spacing: 0.02em;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .input-toggle-btn:hover {
          background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
        }

        .input-toggle-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
        }

        .action-panel {
          grid-column: 1 / -1;
          text-align: center;
          margin: 28px 0;
          padding: 24px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(226, 232, 240, 0.6);
        }

        .run-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          padding: 18px 56px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 24px rgba(59, 130, 246, 0.35);
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .run-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(59, 130, 246, 0.45);
        }

        .run-btn:active:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
        }

        .run-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
        }

        .output-panel {
          grid-column: 1 / -1;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(226, 232, 240, 0.6);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .output-panel:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .output-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
        }

        .output-header h3 {
          margin: 0;
          color: #0f172a;
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .output-textarea {
          min-height: 180px;
          padding: 20px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
          border: 2px solid rgba(226, 232, 240, 0.8);
          border-radius: 12px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Fira Code', monospace;
          font-size: 14px;
          line-height: 1.7;
          white-space: pre-wrap;
          overflow-x: auto;
          margin: 0;
          color: #1e293b;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .output-textarea.success {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border-color: #22c55e;
          color: #14532d;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .output-textarea.error,
        .output-textarea.compile_error,
        .output-textarea.runtime_error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border-color: #ef4444;
          color: #7f1d1d;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .output-textarea.timeout {
          background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
          border-color: #eab308;
          color: #713f12;
          box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .output-textarea.loading {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border-color: #94a3b8;
          color: #475569;
          font-style: italic;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .reusable-code-executor {
            padding: 24px;
            border-radius: 16px;
          }

          .executor-grid {
            gap: 24px;
          }

          .editor-panel,
          .input-panel,
          .output-panel {
            padding: 24px;
          }

          .run-btn {
            padding: 16px 48px;
            font-size: 15px;
          }
        }

        @media (max-width: 768px) {
          .reusable-code-executor {
            padding: 20px;
            border-radius: 14px;
          }

          .executor-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .editor-panel,
          .input-panel,
          .output-panel,
          .action-panel {
            padding: 20px;
            border-radius: 14px;
          }

          .panel-header,
          .output-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 14px;
          }

          .panel-header h3,
          .output-header h3,
          .input-panel h3 {
            font-size: 1.2rem;
          }

          .editor-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .code-textarea,
          .input-textarea {
            min-height: 160px;
            font-size: 13px;
            padding: 16px;
          }

          .output-textarea {
            min-height: 140px;
            font-size: 13px;
            padding: 16px;
          }

          .run-btn {
            width: 100%;
            padding: 16px 40px;
            font-size: 15px;
          }

          .action-panel {
            padding: 20px;
          }
        }

        @media (max-width: 480px) {
          .reusable-code-executor {
            padding: 16px;
            border-radius: 12px;
          }

          .executor-grid {
            gap: 16px;
          }

          .editor-panel,
          .input-panel,
          .output-panel,
          .action-panel {
            padding: 16px;
            border-radius: 12px;
          }

          .panel-header h3,
          .output-header h3,
          .input-panel h3 {
            font-size: 1.1rem;
          }

          .code-textarea,
          .input-textarea {
            min-height: 140px;
            font-size: 12px;
            padding: 14px;
          }

          .output-textarea {
            min-height: 120px;
            font-size: 12px;
            padding: 14px;
          }

          .run-btn {
            padding: 14px 32px;
            font-size: 14px;
          }

          .reset-btn {
            padding: 10px 18px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default ReusableCodeExecutor;
